// ============================================
// ConverseTech Shared - Gemini Function Tools
// ============================================

import type { TrainingMode } from './types';

/**
 * Function call request from Gemini
 */
export interface GeminiFunctionCall {
  name: string;
  args: Record<string, unknown>;
}

/**
 * Function call response to Gemini
 */
export interface GeminiFunctionResponse {
  name: string;
  response: Record<string, unknown>;
}

/**
 * Tool definition for Gemini API
 */
export interface GeminiToolDefinition {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, {
      type: string;
      description: string;
      enum?: string[];
    }>;
    required: string[];
  };
}

// === Function Definitions ===

/**
 * Log what the user said for transcription purposes.
 * This is a workaround since inputAudioTranscription isn't reliable.
 * Used in ALL modes.
 */
export const logUserSpeech: GeminiToolDefinition = {
  name: 'log_user_speech',
  description: 'IMPORTANT: Call this function EVERY TIME the user finishes speaking to log their exact words. This is critical for tracking progress and calculating metrics. Always transcribe what the user said as accurately as possible before responding.',
  parameters: {
    type: 'object',
    properties: {
      user_speech: {
        type: 'string',
        description: 'Exact transcription of what the user just said',
      },
      topic: {
        type: 'string',
        description: 'Brief topic or subject the user was talking about',
      },
    },
    required: ['user_speech'],
  },
};

/**
 * Interrupt user when they're missing a required section in their explanation.
 * Used in technical_explanation mode.
 */
export const interruptMissingSection: GeminiToolDefinition = {
  name: 'interrupt_missing_section',
  description: 'Interrupt the user when they have skipped a required section (context, problem, solution, trade-offs) in their technical explanation. Use this to guide them back to the structured format.',
  parameters: {
    type: 'object',
    properties: {
      missing_section: {
        type: 'string',
        description: 'The section that the user has skipped',
        enum: ['context', 'problem', 'solution', 'trade-offs'],
      },
      suggestion: {
        type: 'string',
        description: 'Brief suggestion on what the user should cover in this section',
      },
    },
    required: ['missing_section'],
  },
};

/**
 * Request the user to repeat a phrase with better pronunciation or structure.
 * Used in all modes.
 */
export const requestRepetition: GeminiToolDefinition = {
  name: 'request_repetition',
  description: 'Ask the user to repeat a phrase or sentence with better pronunciation, structure, or phrasing. Provide the improved version for them to practice.',
  parameters: {
    type: 'object',
    properties: {
      original_phrase: {
        type: 'string',
        description: 'The phrase the user said that needs improvement',
      },
      improved_phrase: {
        type: 'string',
        description: 'The improved version of the phrase for the user to repeat',
      },
      reason: {
        type: 'string',
        description: 'Brief explanation of why this improvement is better',
      },
    },
    required: ['original_phrase', 'improved_phrase'],
  },
};

/**
 * Warn the user about time limits.
 * Used in mock_interview and duolingo_test modes.
 */
export const timeLimitWarning: GeminiToolDefinition = {
  name: 'time_limit_warning',
  description: 'Warn the user about approaching or exceeded time limits for their response.',
  parameters: {
    type: 'object',
    properties: {
      seconds_remaining: {
        type: 'number',
        description: 'Seconds remaining before the time limit',
      },
      time_exceeded: {
        type: 'boolean',
        description: 'Whether the time limit has been exceeded',
      },
      message: {
        type: 'string',
        description: 'Custom warning message',
      },
    },
    required: ['seconds_remaining'],
  },
};

/**
 * Provide a new task prompt to the user.
 * Used in duolingo_test mode.
 */
export const provideTaskPrompt: GeminiToolDefinition = {
  name: 'provide_task_prompt',
  description: 'Present a new DET-style task to the user with instructions and time limit.',
  parameters: {
    type: 'object',
    properties: {
      task_type: {
        type: 'string',
        description: 'Type of DET task',
        enum: ['read_aloud', 'short_answer', 'extended_speaking'],
      },
      prompt: {
        type: 'string',
        description: 'The task prompt or question for the user',
      },
      time_limit_seconds: {
        type: 'number',
        description: 'Time limit for this task in seconds',
      },
      instructions: {
        type: 'string',
        description: 'Additional instructions for the task',
      },
    },
    required: ['task_type', 'prompt', 'time_limit_seconds'],
  },
};

/**
 * Report the score for a completed DET phase.
 * Used in duolingo_test mode.
 */
export const reportDetPhaseScore: GeminiToolDefinition = {
  name: 'report_det_phase_score',
  description: 'Report the score for the DET phase that just completed. Call this immediately after each phase ends to provide feedback to the user.',
  parameters: {
    type: 'object',
    properties: {
      phase: {
        type: 'string',
        description: 'The phase that just completed',
        enum: ['read_aloud', 'short_answer', 'extended_speaking'],
      },
      score: {
        type: 'number',
        description: 'Score from 0-100 for this phase based on fluency, accuracy, and completeness',
      },
      feedback: {
        type: 'string',
        description: 'Brief constructive feedback for the user about their performance',
      },
      over_time: {
        type: 'boolean',
        description: 'Whether the user exceeded the time limit for this phase',
      },
    },
    required: ['phase', 'score'],
  },
};

/**
 * Ask a follow-up interview question.
 * Used in mock_interview mode.
 */
export const interviewFollowup: GeminiToolDefinition = {
  name: 'interview_followup',
  description: 'Ask a follow-up question to probe deeper into the candidate\'s response during a mock interview.',
  parameters: {
    type: 'object',
    properties: {
      followup_type: {
        type: 'string',
        description: 'Type of follow-up question',
        enum: ['clarification', 'depth', 'metrics', 'edge_case', 'alternative'],
      },
      question: {
        type: 'string',
        description: 'The follow-up question to ask',
      },
      context: {
        type: 'string',
        description: 'What part of the user\'s response this follows up on',
      },
    },
    required: ['followup_type', 'question'],
  },
};

/**
 * Get the tools available for a specific training mode.
 */
export function getToolsForMode(mode: TrainingMode): GeminiToolDefinition[] {
  // logUserSpeech is included in ALL modes for transcription workaround
  const commonTools = [logUserSpeech, requestRepetition];

  switch (mode) {
    case 'technical_explanation':
      return [...commonTools, interruptMissingSection];

    case 'mock_interview':
      return [...commonTools, timeLimitWarning, interviewFollowup];

    case 'duolingo_test':
      return [...commonTools, timeLimitWarning, provideTaskPrompt, reportDetPhaseScore];

    case 'conversation':
    default:
      return commonTools;
  }
}

/**
 * Format tools for Gemini API setup message.
 */
export function formatToolsForGemini(tools: GeminiToolDefinition[]): object {
  return {
    functionDeclarations: tools.map(tool => ({
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    })),
  };
}
